import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Button,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    LinearProgress,
    Stack,
    IconButton
} from '@mui/material';
import { API_ENDPOINTS } from '../../../utils/config';
import { useAuth } from '../../../Context/AuthContext';
import AgentCodeEditor from './AgentCodeEditor';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    GRID_SIZE as SOLVER_GRID_SIZE,
    EMPTY_CELL as SOLVER_EMPTY,
    createSolvedPuzzle as buildSolvedPuzzle,
    isPuzzleSolved as boardIsSolved,
    isSolvable as boardIsSolvable,
    adjacentPositions,
    solvePuzzleAsync,
} from './puzzleMasterSolver';
import {
    IDLE_ABANDON_MS,
    IDLE_ABANDON_MESSAGE,
    manualPlacementScore,
    puzzleHistoryTick,
    isPuzzleTimerRunning,
    displayedPuzzleScore,
    humanFinishUploadArgs,
    aiSolveUploadArgs,
    idleAbandonUploadArgs,
} from './puzzleMasterRun';
import {
    RestartAlt,
    Stop,
    EmojiEvents,
    Timer,
    TouchApp,
    Psychology,
    PlayArrow,
    Pause,
    SkipNext,
    CloudUpload,
    Loop,
    TipsAndUpdates,
    Lock,
    AccessTime
} from '@mui/icons-material';
import { normalizeLastRunDoc, shouldShowReplay } from '../useArcadeAgentSession';
import { bestLeaderboardElapsedTime, uploadErrorInfo } from '../arcadeTheme';
import UploadAccountLinks from '../UploadAccountLinks';
import ManualSaveNotice from '../ManualSaveNotice';
import {
    ArcadeReplayDialog,
    LAST_RUN_LOOP_BUTTON_SX,
    LeaderboardWatchButton,
    useArcadeReplay,
} from '../arcadeReplay';
import {
    GM_GAME_ID,
    createTilesSession,
    deleteTilesSession,
    fromGmBoard,
    inputTilesSession,
} from './tilesGmClient';
import { gmStartErrorMessage, useGmLease } from '../gmLease';
import GmLeaseNotice from '../GmLeaseNotice';
import { COLOR, GAME_ACCENT } from '../ui/tokens';
import { ArcadeShellLayout } from '../ArcadeGameShell';
import LeaderboardPanel from '../ui/LeaderboardPanel';
import ResultView from '../ui/ResultView';
import UiButton from '../ui/Button';
import { isGuestUser, useKeyboardStart } from '../manualPlay';

// ---------------------------------------------------------------------------
// Boilerplate template for the sliding tiles code editor
// ---------------------------------------------------------------------------
const { header: PUZZLEMASTER_HEADER, template: GAME_TEMPLATE, footer: PUZZLEMASTER_FOOTER } = GAME_BOILERPLATE.puzzle_master;


// Theme Colors
const ACCENT = GAME_ACCENT.sliding_tiles_puzzle;

const THEME = {
    bg: COLOR.bg,
    cardBg: COLOR.surface,
    border: COLOR.border,
    primary: COLOR.primary,
    secondary: COLOR.textMuted,
    accent: COLOR.danger,
    grid: COLOR.border
};

// Separate PuzzleGrid component for better state management and re-rendering
const PuzzleGrid = ({
    puzzle,
    isValidMove,
    makeMove,
    isShowingSolution,
    solution,
    solutionStep,
    getTileColor,
    GRID_SIZE
}) => {
    // Force re-render when puzzle changes
    const [renderKey, setRenderKey] = useState(0);

    useEffect(() => {
        setRenderKey(prev => prev + 1);
    }, [puzzle]);

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4
        }}>
            <Paper sx={{
                p: 2,
                backgroundColor: 'rgba(255,255,255,0.02)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: `1px solid ${THEME.border}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
            }}>
                <Grid container spacing={1} sx={{ width: 340, height: 340 }} key={renderKey}>
                    {puzzle.map((tile, index) => {
                        const isNextSolutionMove = isShowingSolution &&
                            solutionStep < solution.length &&
                            solution[solutionStep] === tile;

                        return (
                            <Grid item xs={3} key={`${index}-${tile}-${renderKey}`}>
                                <Paper
                                    elevation={tile ? 4 : 0}
                                    sx={{
                                        height: 75,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: tile && isValidMove(index) ? 'pointer' : 'default',
                                        backgroundColor: tile ? getTileColor(tile) : 'transparent',
                                        color: tile ? 'white' : 'transparent',
                                        fontWeight: 'bold',
                                        fontSize: '1.5rem',
                                        textShadow: tile ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none',
                                        border: isNextSolutionMove
                                            ? `3px solid ${THEME.accent}`
                                            : tile
                                                ? '1px solid rgba(255,255,255,0.1)'
                                                : '2px dashed rgba(255,255,255,0.05)',
                                        borderRadius: 2,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        transform: isNextSolutionMove ? 'scale(1.05)' : 'scale(1)',
                                        '&:hover': tile && isValidMove(index) ? {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                            zIndex: 1
                                        } : {},
                                        position: 'relative',
                                        boxShadow: isNextSolutionMove
                                            ? `0 0 20px ${THEME.accent}40`
                                            : tile
                                                ? '0 2px 4px rgba(0,0,0,0.2)'
                                                : 'none'
                                    }}
                                    onClick={() => tile && makeMove(index)}
                                >
                                    {tile}
                                    {/* Indicator dot for valid moves */}
                                    {tile && isValidMove(index) && !isNextSolutionMove && (
                                        <Box sx={{
                                            position: 'absolute',
                                            top: 4,
                                            right: 4,
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                        }} />
                                    )}
                                    {isNextSolutionMove && (() => {
                                        const emptyPos = puzzle.indexOf(null);
                                        const row = Math.floor(index / GRID_SIZE);
                                        const col = index % GRID_SIZE;
                                        const emptyRow = Math.floor(emptyPos / GRID_SIZE);
                                        const emptyCol = emptyPos % GRID_SIZE;

                                        let arrowDirection = '->';

                                        if (emptyRow < row) { arrowDirection = '^'; }
                                        else if (emptyRow > row) { arrowDirection = 'v'; }
                                        else if (emptyCol < col) { arrowDirection = '<-'; }
                                        else if (emptyCol > col) { arrowDirection = '->'; }

                                        return (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: -6,
                                                right: -6,
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: THEME.accent,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                fontSize: '1.2rem',
                                                zIndex: 2,
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                                            }}>
                                                {arrowDirection}
                                            </Box>
                                        );
                                    })()}
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>
        </Box>
    );
};

const PuzzleMaster = () => {
    // Shared Sliding Tiles GM: started on Start, released when solved / leaving.
    const gmLease = useGmLease(GM_GAME_ID);
    const GRID_SIZE = SOLVER_GRID_SIZE;
    const EMPTY_CELL = SOLVER_EMPTY;

    const createSolvedPuzzle = () => buildSolvedPuzzle(GRID_SIZE);

    const [puzzle, setPuzzle] = useState(createSolvedPuzzle());
    const [puzzleKey] = useState(0);
    const [moves, setMoves] = useState(0);
    const [startTime, setStartTime] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [isGameWon, setIsGameWon] = useState(false);
    const [isAbandoned, setIsAbandoned] = useState(false);
    const [timerFrozen, setTimerFrozen] = useState(false);
    const [frozenScore, setFrozenScore] = useState(null);
    const [gameOverTitle, setGameOverTitle] = useState('');
    const [showWinDialog, setShowWinDialog] = useState(false);
    const [solution, setSolution] = useState([]);
    const [solutionStep, setSolutionStep] = useState(0);
    const [isShowingSolution, setIsShowingSolution] = useState(false);
    const [isSolving, setIsSolving] = useState(false);
    const [solveMessage, setSolveMessage] = useState('');
    const [solveProgress, setSolveProgress] = useState('');
    const [autoPlay, setAutoPlay] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [, setSolutionInterval] = useState(null);
    const [agentInitState] = useState(null);
    const [isLoopingSolution, setIsLoopingSolution] = useState(false);
    const [, setLastAgentReplay] = useState(null);
    const [playingEntryId, setPlayingEntryId] = useState(null);
    const [lastExecutedAgentName, setLastExecutedAgentName] = useState('');
    const [lastExecutedTime, setLastExecutedTime] = useState(null);
    const [lastExecutedScore, setLastExecutedScore] = useState(null);
    const [lastRunEntry, setLastRunEntry] = useState(null);
    const [lastRunLoading, setLastRunLoading] = useState(true);
    const lastRunRequestIdRef = useRef(0);
    const replay = useArcadeReplay();
    const { token, user } = useAuth();
    const refreshPuzzleBoard = useCallback(async () => {
        try {
            const res = await fetch(API_ENDPOINTS.SLIDING_TILES_LEADERBOARD);
            const data = await res.json();
            setScoreboard(data.leaderboard || data || []);
        } catch {
            /* keep existing board */
        }
    }, []);
    const puzzleSession = useMemo(() => ({ refreshLeaderboard: refreshPuzzleBoard }), [refreshPuzzleBoard]);
    const recorder = useManualRunRecorder({
        gameId: 'sliding_tiles_puzzle',
        copyToMoveHistory: true,
        session: puzzleSession,
    });


    // Upload & scoreboard state
    const [scoreboard, setScoreboard] = useState([]);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadInputTab, setUploadInputTab] = useState('upload'); // 'upload' | 'editor'
    const [uploadMessage, setUploadMessage] = useState('');
    const [uploadNeedsAccount, setUploadNeedsAccount] = useState(false);

    // Refs
    const puzzleRef = useRef(puzzle);
    const solutionRef = useRef(solution);
    const solutionStepRef = useRef(solutionStep);
    const isLoopingSolutionRef = useRef(isLoopingSolution);
    const agentInitRef = useRef(agentInitState);
    const solutionIntervalRef = useRef(null);
    const stoppedRef = useRef(false); // Track if solution was stopped
    const solveCancelRef = useRef(false);
    const lastUserInputAtRef = useRef(null);
    const abandonedRef = useRef(false);
    const idleUploadedRef = useRef(false);
    const aiSolveRef = useRef(null);
    const frozenScoreRef = useRef(null);
    const recorderRef = useRef(recorder);
    const movesRef = useRef(moves);
    const startTimeRef = useRef(startTime);
    const isGameStartedRef = useRef(isGameStarted);
    const isGameWonRef = useRef(isGameWon);
    const gmSessionIdRef = useRef(null);
    const moveInFlightRef = useRef(false);
    recorderRef.current = recorder;
    movesRef.current = moves;
    startTimeRef.current = startTime;
    isGameStartedRef.current = isGameStarted;
    isGameWonRef.current = isGameWon;

    const boardScore = displayedPuzzleScore({
        scoreFrozen: frozenScore != null,
        scoreAtQuit: frozenScore,
        board: puzzle,
    });
    // Only the current run has a score: before Start and after Reset the
    // solved demo board used to show a stale value (1241, 509, ...).
    const liveScore = (isGameStarted || frozenScore != null) ? boardScore : '—';
    const runLive = isGameStarted && !isGameWon && !isAbandoned;
    const [manualResult, setManualResult] = useState(null);
    const bestLoggedTime = useMemo(
        () => bestLeaderboardElapsedTime(scoreboard),
        [scoreboard]
    );

    // Sync refs
    useEffect(() => { puzzleRef.current = puzzle; }, [puzzle]);
    useEffect(() => { solutionRef.current = solution; }, [solution]);
    useEffect(() => { solutionStepRef.current = solutionStep; }, [solutionStep]);
    useEffect(() => { isLoopingSolutionRef.current = isLoopingSolution; }, [isLoopingSolution]);
    useEffect(() => { agentInitRef.current = agentInitState; }, [agentInitState]);

    // Cleanup GM session on unmount
    useEffect(() => () => {
        const sid = gmSessionIdRef.current;
        gmSessionIdRef.current = null;
        deleteTilesSession(sid);
    }, []);

    // Timer runs only after Start, while the user is still playing.
    useEffect(() => {
        let interval;
        if (startTime && isPuzzleTimerRunning({
            isGameStarted,
            isGameWon,
            isAbandoned,
            timerFrozen,
            autoPlay,
            isPaused,
        })) {
            interval = setInterval(() => {
                setElapsedTime(Date.now() - startTime);
            }, 100);
        }
        return () => clearInterval(interval);
    }, [isGameStarted, isGameWon, isAbandoned, timerFrozen, startTime, autoPlay, isPaused]);

    // Game Logic Functions (isPuzzleSolved, getEmptyPosition, etc.)...
    // ... (Keeping core logic identical for stability)
    const isPuzzleSolved = (puzzleState) => boardIsSolved(puzzleState, GRID_SIZE);

    const getEmptyPosition = (puzzleState) => puzzleState.indexOf(EMPTY_CELL);

    const getAdjacentPositions = (position) => adjacentPositions(position, GRID_SIZE);

    const isValidMove = (position) => {
        if (!isGameStarted || isAbandoned || isGameWon || isShowingSolution || isSolving || autoPlay || timerFrozen) {
            return false;
        }
        const emptyPos = getEmptyPosition(puzzle);
        return getAdjacentPositions(emptyPos).includes(position);
    };

    const makeMove = async (position) => {
        if (!isGameStarted || isAbandoned || isShowingSolution || isSolving || autoPlay || isGameWon || timerFrozen) {
            return;
        }
        if (!getAdjacentPositions(getEmptyPosition(puzzle)).includes(position)) return;
        if (moveInFlightRef.current) return;
        const sid = gmSessionIdRef.current;
        if (!sid) return;

        lastUserInputAtRef.current = Date.now();
        moveInFlightRef.current = true;
        try {
            const data = await inputTilesSession(sid, position);
            const newPuzzle = fromGmBoard(data.puzzle);
            const tile = puzzle[position];
            setPuzzle(newPuzzle);
            puzzleRef.current = newPuzzle;
            const moveCount = typeof data.move_count === 'number' ? data.move_count : moves + 1;
            setMoves(moveCount);
            recorder.recordTick(puzzleHistoryTick(tile == null ? position : tile, 'user', newPuzzle));

            if (data.is_game_won || boardIsSolved(newPuzzle, GRID_SIZE)) {
                setIsGameWon(true);
                const finalTime = typeof data.elapsed_time === 'number' && data.elapsed_time > 0
                    ? data.elapsed_time
                    : (Date.now() - startTime);
                setElapsedTime(finalTime);
                setGameOverTitle('');
                setShowWinDialog(true);
                const gmScore = typeof data.score === 'number' ? data.score : data.current_score;
                const upload = humanFinishUploadArgs({ board: newPuzzle, moves: moveCount, elapsedMs: finalTime });
                if (Number.isFinite(gmScore)) upload.score = gmScore;
                frozenScoreRef.current = upload.score;
                setFrozenScore(upload.score);
                recorder.finalizeAndUpload(upload);
                const done = gmSessionIdRef.current;
                gmSessionIdRef.current = null;
                deleteTilesSession(done);
            }
        } catch (err) {
            console.warn('Tiles GM move failed', err);
        } finally {
            moveInFlightRef.current = false;
        }
    };

    const startGame = async () => {
        try {
            if (gmSessionIdRef.current) {
                await deleteTilesSession(gmSessionIdRef.current);
                gmSessionIdRef.current = null;
            }
            const data = await createTilesSession();
            if (!data?.session_id) throw new Error('Tiles GM missing session_id');
            gmSessionIdRef.current = data.session_id;
            const shuffled = fromGmBoard(data.puzzle || data.init_state);
            const now = Date.now();
            abandonedRef.current = false;
            idleUploadedRef.current = false;
            aiSolveRef.current = null;
            frozenScoreRef.current = null;
            lastUserInputAtRef.current = now;
            setPuzzle(shuffled);
            puzzleRef.current = shuffled;
            setMoves(0);
            setFrozenScore(null);
            setElapsedTime(0);
            setIsGameWon(false);
            setIsAbandoned(false);
            setTimerFrozen(false);
            setGameOverTitle('');
            setShowWinDialog(false);
            setStartTime(now);
            setIsGameStarted(true);
            setManualResult(null);
            setSolveMessage('');
            setSolveProgress('');
            stopSolution({ resetStats: false, skipAiUpload: true });
            recorder.reset({ init_state: shuffled });
        } catch (err) {
            console.error(err);
            setSolveMessage(gmStartErrorMessage(err));
        }
    };

    // End Game: same clean-up as Reset (nothing is submitted), but the run's
    // score stays on screen.
    const endRun = () => {
        const result = { score: boardScore, moves };
        resetGame();
        setManualResult(result);
    };

    // Enter / Space start when idle (menu or after End / result).
    useKeyboardStart({
        active: !runLive && !isSolving && !isShowingSolution,
        onStart: startGame,
    });

    const resetGame = () => {
        setManualResult(null);
        solveCancelRef.current = true;
        abandonedRef.current = false;
        idleUploadedRef.current = false;
        aiSolveRef.current = null;
        frozenScoreRef.current = null;
        lastUserInputAtRef.current = null;
        const sid = gmSessionIdRef.current;
        gmSessionIdRef.current = null;
        deleteTilesSession(sid);
        setPuzzle(createSolvedPuzzle());
        setMoves(0);
        setFrozenScore(null);
        setElapsedTime(0);
        setIsGameStarted(false);
        setIsGameWon(false);
        setIsAbandoned(false);
        setTimerFrozen(false);
        setGameOverTitle('');
        setStartTime(null);
        setShowWinDialog(false);
        setSolveMessage('');
        setSolveProgress('');
        stopSolution({ resetStats: false, skipAiUpload: true });
    };

    const formatAuthTimestamp = useCallback((ts) => {
        if (!ts) return '-';
        let date;
        // If string (ISO) and missing Z/offset, assume UTC (server standard)
        if (typeof ts === 'string' && ts.includes('T') && !ts.endsWith('Z') && !ts.includes('+')) {
            date = new Date(ts + 'Z');
        } else {
            date = new Date(ts);
        }
        return date.toLocaleString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit', hour12: true
        });
    }, []);

    const userId = user?._id || user?.id || null;
    const userRole = user?.user_type || user?.role || (token ? 'registered' : 'guest');

    const hydrateLastRun = useCallback((doc) => {
        const normalized = normalizeLastRunDoc(doc);
        if (!normalized) {
            setLastExecutedAgentName('');
            setLastExecutedTime(null);
            setLastExecutedScore(null);
            setLastAgentReplay(null);
            setLastRunEntry(null);
            return;
        }
        setLastExecutedAgentName(normalized.name || '');
        setLastExecutedTime(normalized.time ? new Date(normalized.time).getTime() : Date.now());
        setLastExecutedScore(normalized.score);
        setLastRunEntry(normalized.raw || null);
        const raw = normalized.raw || {};
        const moveHistory = Array.isArray(raw.move_history)
            ? raw.move_history
            : Array.isArray(raw.history)
                ? raw.history
                : [];
        if (Array.isArray(raw.init_state) && moveHistory.length > 0) {
            setLastAgentReplay({
                init_state: raw.init_state,
                move_history: moveHistory,
            });
        } else {
            setLastAgentReplay((prev) => (prev ? prev : null));
        }
    }, []);

    const fetchLastRun = useCallback(async () => {
        const requestId = lastRunRequestIdRef.current + 1;
        lastRunRequestIdRef.current = requestId;
        setLastRunLoading(true);
        try {
            const res = await fetch(API_ENDPOINTS.LAST_RUN('sliding_tiles_puzzle', { userId, role: userRole }));
            if (!res.ok) throw new Error(`last run fetch failed (${res.status})`);
            const data = await res.json();
            if (lastRunRequestIdRef.current !== requestId) return;
            hydrateLastRun(data?.run);
        } catch (err) {
            if (lastRunRequestIdRef.current !== requestId) return;
            console.warn('PuzzleMaster last-run fetch failed', err);
        } finally {
            if (lastRunRequestIdRef.current === requestId) {
                setLastRunLoading(false);
            }
        }
    }, [hydrateLastRun, userId, userRole]);

    const formatTime = (milliseconds) => {
        const seconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const ms = Math.floor((milliseconds % 1000) / 10);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
    };

    const getTileColor = (number) => {
        // Darker, more professional color palette
        const colors = [
            '#2e7d32', '#1565c0', '#c62828', '#f9a825',
            '#6a1b9a', '#00838f', '#ad1457', '#283593',
            '#4e342e', '#37474f', '#0277bd', '#ef6c00',
            '#00695c', '#4527a0', '#d84315'
        ];
        return colors[(number - 1) % colors.length] + 'dd'; // Add transparency
    };

    const playBuiltinSolution = (seq) => {
        stoppedRef.current = false;
        setPlayingEntryId(null);
        setIsLoopingSolution(false);
        isLoopingSolutionRef.current = false;
        setSolution(seq);
        solutionRef.current = seq;
        setSolutionStep(0);
        solutionStepRef.current = 0;
        setIsShowingSolution(true);
        setAutoPlay(true);
        setIsPaused(false);
        setSolveProgress(`${seq.length} moves`);
        if (solutionIntervalRef.current) {
            clearInterval(solutionIntervalRef.current);
        }
        const interval = setInterval(() => { executeNextStep(); }, 600);
        solutionIntervalRef.current = interval;
        setSolutionInterval(interval);
    };

    const cancelSolve = () => {
        solveCancelRef.current = true;
        setIsSolving(false);
        setSolveProgress('');
        setSolveMessage('Solve cancelled.');
        if (aiSolveRef.current && !aiSolveRef.current.active) {
            aiSolveRef.current = null;
            frozenScoreRef.current = null;
            setFrozenScore(null);
            setTimerFrozen(false);
            lastUserInputAtRef.current = Date.now();
        }
    };

    const handleSolve = async () => {
        if (!isGameStarted || isGameWon || isAbandoned || isSolving || isShowingSolution) return;
        const board = puzzleRef.current || puzzle;
        setSolveMessage('');
        if (isPuzzleSolved(board)) {
            setSolveMessage('Puzzle is already solved.');
            return;
        }
        if (!boardIsSolvable(board, GRID_SIZE)) {
            setSolveMessage('This board is unsolvable. Press Start for a new puzzle.');
            return;
        }

        const quitMoves = moves;
        const quitElapsed = startTime ? Date.now() - startTime : elapsedTime;
        const boardAtQuit = board.slice();
        const quitScore = manualPlacementScore(boardAtQuit);
        aiSolveRef.current = {
            pending: true,
            active: false,
            finalized: false,
            scoreAtQuit: quitScore,
            boardAtQuit,
            userMoves: quitMoves,
            elapsedAtQuit: quitElapsed,
        };
        frozenScoreRef.current = quitScore;
        setFrozenScore(quitScore);
        setMoves(quitMoves);
        setTimerFrozen(true);
        setElapsedTime(quitElapsed);

        solveCancelRef.current = false;
        stoppedRef.current = false;
        setIsSolving(true);
        setSolveProgress('Solving…');

        const result = await solvePuzzleAsync(board, {
            onProgress: ({ nodes }) => {
                setSolveProgress(`Solving… ${nodes} searched`);
            },
            isCancelled: () => solveCancelRef.current || stoppedRef.current,
        });

        if (!result || result.cancelled || solveCancelRef.current) {
            setIsSolving(false);
            if (!solveMessage) setSolveProgress('');
            if (aiSolveRef.current && !aiSolveRef.current.active) {
                aiSolveRef.current = null;
                frozenScoreRef.current = null;
                setFrozenScore(null);
                setTimerFrozen(false);
                lastUserInputAtRef.current = Date.now();
            }
            return;
        }

        setIsSolving(false);

        if (result.unsolvable) {
            setSolveProgress('');
            setSolveMessage('This board is unsolvable. Press Start for a new puzzle.');
            aiSolveRef.current = null;
            frozenScoreRef.current = null;
            setFrozenScore(null);
            setTimerFrozen(false);
            lastUserInputAtRef.current = Date.now();
            return;
        }
        if (!result.path || result.path.length === 0) {
            setSolveProgress('');
            setSolveMessage(result.solved
                ? 'Puzzle is already solved.'
                : 'No solution found. Press Start for a new puzzle.');
            aiSolveRef.current = null;
            frozenScoreRef.current = null;
            setFrozenScore(null);
            setTimerFrozen(false);
            lastUserInputAtRef.current = Date.now();
            return;
        }

        if (aiSolveRef.current) {
            aiSolveRef.current.pending = false;
            aiSolveRef.current.active = true;
        }
        playBuiltinSolution(result.path);
    };

    const finishAiSolvePlayback = (aiFinished = true) => {
        const session = aiSolveRef.current;
        if (!session || session.finalized) return false;
        session.finalized = true;
        session.active = false;
        session.pending = false;
        recorderRef.current.finalizeAndUpload({
            ...aiSolveUploadArgs({
                board: session.boardAtQuit,
                userMoves: session.userMoves,
                elapsedAtQuit: session.elapsedAtQuit,
                aiFinished,
            }),
        });
        setIsGameStarted(false);
        setTimerFrozen(true);
        setElapsedTime(session.elapsedAtQuit);
        setMoves(session.userMoves);
        frozenScoreRef.current = session.scoreAtQuit;
        setFrozenScore(session.scoreAtQuit);
        return true;
    };

    const executeNextStep = () => {
        // Check if stopped - bail out immediately
        if (stoppedRef.current) {
            return;
        }

        const sol = solutionRef.current || [];
        const step = solutionStepRef.current || 0;
        const currentPuzzle = puzzleRef.current || [];
        const aiSession = aiSolveRef.current;

        if (step >= sol.length) {
            if (isLoopingSolutionRef.current && agentInitRef.current) {
                const normalized = agentInitRef.current.map(v => v === 999 ? EMPTY_CELL : v);
                setPuzzle(normalized);
                puzzleRef.current = normalized;
                setMoves(0);
                setIsGameWon(false);
                setStartTime(Date.now());
                setElapsedTime(0);
                solutionStepRef.current = 0;
                setSolutionStep(0);
            } else if (aiSession?.active) {
                finishAiSolvePlayback(true);
                stopSolution({ resetStats: false, skipAiUpload: true });
            } else {
                stopSolution();
            }
            return;
        }

        const move = sol[step];
        let movePosition = -1;
        let movedTile = null;

        if (typeof move === 'object' && move !== null) {
            if (Number.isInteger(move.to)) {
                movePosition = move.to;
                movedTile = currentPuzzle[move.to];
            } else if (Number.isInteger(move.tile)) {
                const tile = move.tile === 999 ? EMPTY_CELL : move.tile;
                movedTile = tile;
                movePosition = currentPuzzle.indexOf(tile);
            }
        } else if (Number.isInteger(move)) {
            const tile = move === 999 ? EMPTY_CELL : move;
            movedTile = tile;
            let idx = currentPuzzle.indexOf(tile);
            if (idx === -1 && move >= 0 && move < GRID_SIZE * GRID_SIZE) {
                idx = move;
                movedTile = currentPuzzle[idx];
            }
            movePosition = idx;
        }

        if (movePosition < 0 || movePosition >= GRID_SIZE * GRID_SIZE) {
            solutionStepRef.current += 1;
            setSolutionStep(solutionStepRef.current);
            return;
        }

        const emptyPos = currentPuzzle.indexOf(EMPTY_CELL);
        if (!getAdjacentPositions(emptyPos).includes(movePosition)) {
            solutionStepRef.current += 1;
            setSolutionStep(solutionStepRef.current);
            return;
        }

        const newPuzzle = [...currentPuzzle];
        [newPuzzle[emptyPos], newPuzzle[movePosition]] = [newPuzzle[movePosition], newPuzzle[emptyPos]];

        setPuzzle(newPuzzle);
        puzzleRef.current = newPuzzle;

        const scoreFrozen = frozenScoreRef.current != null
            || Boolean(aiSession?.active || aiSession?.pending);
        if (aiSession?.active || aiSession?.pending) {
            if (aiSession.active) {
                recorderRef.current.recordTick(puzzleHistoryTick(
                    movedTile == null ? movePosition : movedTile,
                    'ai',
                    newPuzzle,
                ));
            }
            setMoves(aiSession.userMoves);
            setFrozenScore(aiSession.scoreAtQuit);
            setElapsedTime(aiSession.elapsedAtQuit);
        } else if (scoreFrozen) {
            setMoves(movesRef.current);
            setFrozenScore(frozenScoreRef.current);
        } else {
            setMoves(m => m + 1);
            // Sync elapsed time with moves during auto-play (600ms per move)
            setElapsedTime(prev => prev + 600);
        }

        if (isPuzzleSolved(newPuzzle)) {
            if (aiSession?.active) {
                finishAiSolvePlayback(true);
                stopSolution({ resetStats: false, skipAiUpload: true });
            } else {
                setIsGameWon(true);
                stopSolution();
            }
            return;
        }

        solutionStepRef.current += 1;
        setSolutionStep(solutionStepRef.current);
    };

    const startAutoPlay = () => {
        if (solutionStep >= solution.length) return;
        setAutoPlay(true);
        setIsPaused(false);
        // Clear any existing interval using ref for reliability
        if (solutionIntervalRef.current) {
            clearInterval(solutionIntervalRef.current);
        }
        const interval = setInterval(() => {
            executeNextStep();
        }, 600);
        solutionIntervalRef.current = interval;
        setSolutionInterval(interval);
    };

    const stopSolution = ({ resetStats = true, skipAiUpload = false } = {}) => {
        if (!skipAiUpload && aiSolveRef.current && !aiSolveRef.current.finalized
            && (aiSolveRef.current.active || aiSolveRef.current.pending)) {
            finishAiSolvePlayback(Boolean(boardIsSolved(puzzleRef.current || puzzle, GRID_SIZE)));
        }

        // Set stopped flag FIRST to prevent executeNextStep from continuing
        stoppedRef.current = true;
        solveCancelRef.current = true;

        setIsShowingSolution(false);
        setAutoPlay(false);
        setIsPaused(false);
        setSolution([]);
        setSolutionStep(0);
        setPlayingEntryId(null);
        setIsLoopingSolution(false);
        isLoopingSolutionRef.current = false;
        setIsSolving(false);
        setSolveProgress('');
        if (resetStats) {
            setMoves(0);
            setElapsedTime(0);
            setIsGameStarted(false);
        }
        // Clear interval using ref for immediate effect
        if (solutionIntervalRef.current) {
            clearInterval(solutionIntervalRef.current);
            solutionIntervalRef.current = null;
        }
        setSolutionInterval(null);
    };

    const pauseAutoPlay = () => {
        setAutoPlay(false);
        setIsPaused(true);
        // Clear interval using ref for immediate effect
        if (solutionIntervalRef.current) {
            clearInterval(solutionIntervalRef.current);
            solutionIntervalRef.current = null;
        }
        setSolutionInterval(null);
    };

    const abandonForIdle = useCallback(() => {
        if (abandonedRef.current || !isGameStartedRef.current || isGameWonRef.current) return;
        if (aiSolveRef.current?.active || aiSolveRef.current?.pending) return;
        abandonedRef.current = true;
        setIsAbandoned(true);
        setIsGameStarted(false);
        setTimerFrozen(true);
        const started = startTimeRef.current;
        const finalTime = started ? Date.now() - started : 0;
        const boardAtQuit = (puzzleRef.current || []).slice();
        const quitScore = manualPlacementScore(boardAtQuit);
        frozenScoreRef.current = quitScore;
        setFrozenScore(quitScore);
        setElapsedTime(finalTime);
        setGameOverTitle(IDLE_ABANDON_MESSAGE);
        setShowWinDialog(true);
        if (!idleUploadedRef.current) {
            idleUploadedRef.current = true;
            recorderRef.current.finalizeAndUpload(
                idleAbandonUploadArgs({ board: boardAtQuit, moves: movesRef.current, elapsedMs: finalTime })
            );
        }
    }, []);

    // 60s with no user input after Start (not during AI solve/playback, not in the menu).
    useEffect(() => {
        const awaitingUser = isGameStarted && !isGameWon && !isAbandoned && !timerFrozen
            && !isShowingSolution && !isSolving && !autoPlay && !playingEntryId;
        if (!awaitingUser) return undefined;
        const interval = setInterval(() => {
            const last = lastUserInputAtRef.current;
            if (last == null) return;
            if (Date.now() - last >= IDLE_ABANDON_MS) {
                abandonForIdle();
            }
        }, 500);
        return () => clearInterval(interval);
    }, [
        isGameStarted, isGameWon, isAbandoned, timerFrozen,
        isShowingSolution, isSolving, autoPlay, playingEntryId, abandonForIdle,
    ]);

    // API Integration
    const fetchScoreboard = async () => {
        try {
            const res = await fetch(API_ENDPOINTS.SLIDING_TILES_LEADERBOARD);
            if (!res.ok) return;
            const data = await res.json();
            if (data && Array.isArray(data.leaderboard)) {
                setScoreboard(data.leaderboard);
            } else if (Array.isArray(data)) {
                setScoreboard(data);
            } else {
                setScoreboard([]);
            }
        } catch (e) {
            setScoreboard([]);
        }
    };

    useEffect(() => { fetchScoreboard(); }, []);
    useEffect(() => { fetchLastRun(); }, [fetchLastRun]);

    const handlePlayLeaderboardEntry = (entry) => {
        replay.openReplay(entry, { loop: false });
    };

    const handleFileChange = (e) => {
        setUploadFile(e.target.files && e.target.files[0] ? e.target.files[0] : null);
        setUploadMessage('');
        setUploadNeedsAccount(false);
    };

    const handleUpload = async () => {
        if (!token) {
            setUploadMessage('Sign in to upload agents.');
            return;
        }
        if (!uploadFile) return setUploadMessage('No file selected');

        setUploading(true);

        const form = new FormData();
        form.append('file', uploadFile);

        try {
            const res = await fetch(API_ENDPOINTS.SLIDING_TILES_UPLOAD_AGENT, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: form
            });

            const json = await res.json().catch(() => ({}));

            if (!res.ok) {
                const { message, needsAccount } = uploadErrorInfo(res.status, json, 'Upload failed');
                setUploadMessage(message);
                setUploadNeedsAccount(needsAccount);
                setUploading(false);
                return;
            }

            // Refresh leaderboard from DB (upload response may omit leaderboard)
            await fetchScoreboard();

            // Handle replay data capture (check both nested entry and flat structure)
            let playData = null;
            if (json && json.entry && json.entry.init_state) {
                playData = json.entry;
            } else if (json && json.init_state) {
                playData = json;
            }

            console.log("Upload Response:", json);

            // Try to find authoritative agent_id from the response - prioritize agent_id over _id
            let authoritativeName = null;

            // First, check if agent_id is directly in the response
            if (json.agent_id) {
                authoritativeName = json.agent_id;
            } else if (json.entry && json.entry.agent_id) {
                authoritativeName = json.entry.agent_id;
            }

            // If not found, try to find from leaderboard using db_ref or _id
            const uploadedId = json.db_ref || (json.entry && json.entry._id) || json._id || json.id;
            if (!authoritativeName && json.leaderboard && Array.isArray(json.leaderboard) && uploadedId) {
                const match = json.leaderboard.find(e => e._id === uploadedId || e.id === uploadedId);
                if (match && match.agent_id) {
                    authoritativeName = match.agent_id;
                }
            }

            let nameToSet = authoritativeName ||
                (json.entry && (json.entry.agent_id || json.entry.agent || json.entry.username)) ||
                (json.agent_id || json.agent || json.username);

            // Only format if we are strictly dealing with a raw ID and NO other name found
            if (nameToSet && !nameToSet.toString().startsWith('agent_') && /^[a-f0-9]{24}$/.test(nameToSet)) {
                // Try to avoid this path by finding the real name
                // But if we must...
                nameToSet = `agent_${nameToSet.substring(0, 8)}`;
            }

            setLastExecutedAgentName(nameToSet || 'Unknown Agent');
            const infoTime = (json.timestamp || (json.entry && json.entry.timestamp) || (json.data && json.data.timestamp));
            setLastExecutedTime(infoTime ? new Date(infoTime).getTime() : Date.now());
            // Try to extract score - prioritize actual score field over moves
            if (json.score !== undefined) setLastExecutedScore(json.score);
            else if (json.entry && json.entry.score !== undefined) setLastExecutedScore(json.entry.score);
            else if (json.moves_count !== undefined) setLastExecutedScore(json.moves_count);
            else if (json.entry && json.entry.moves_count !== undefined) setLastExecutedScore(json.entry.moves_count);
            else setLastExecutedScore('-');

            if (playData) {
                setLastAgentReplay({
                    init_state: playData.init_state,
                    move_history: playData.move_history || []
                });
            }
            await fetchLastRun();
        } catch (err) {
            console.error(err);
            setUploadMessage('Upload failed');
        }
        setUploading(false);
    };

    const handleViewLastPlay = () => {
        if (!lastRunEntry) return setUploadMessage('No previous agent replay available.');
        replay.openReplay(lastRunEntry, { loop: false });
    };

    const handleLoopLastPlay = () => {
        if (!lastRunEntry) return setUploadMessage('No previous agent replay available.');
        replay.openReplay(lastRunEntry, { loop: true });
    };

    return (
        <ArcadeShellLayout
            title="Sliding Tiles"
            titleAccent="Puzzle"
            subtitle="Solve the 15-puzzle manually or upload your Python agent"
            accentColor={ACCENT}
            leaderboard={(
                            <Stack spacing={3}>
                                <LeaderboardPanel
                                    entries={scoreboard}
                                    accentColor={ACCENT}
                                    formatScore={(entry) => String(entry.score || 0)}
                                    formatName={(entry, idx) => entry.agent || entry.username || entry.agent_id || `Agent ${idx + 1}`}
                                    formatTime={(entry) => formatAuthTimestamp(entry.timestamp || entry.created_at || entry.createdAt)}
                                    emptyHint="No scores yet."
                                    limit={10}
                                    renderAction={(entry) => (
                                        <LeaderboardWatchButton
                                            entry={entry}
                                            onWatch={handlePlayLeaderboardEntry}
                                            accentColor={THEME.secondary}
                                        />
                                    )}
                                />

                                                                <Paper sx={{
                                    p: 2,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 3
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: THEME.secondary, mb: 2, letterSpacing: 1 }}>Live stats</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmojiEvents fontSize="inherit" /> Score
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: THEME.primary }} data-testid="puzzle-live-score">{liveScore}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <TouchApp fontSize="inherit" /> Moves
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: 'white' }} data-testid="puzzle-live-moves">{moves}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Timer fontSize="inherit" /> Time
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: THEME.primary }} data-testid="puzzle-elapsed-time">{formatTime(elapsedTime)}</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <AccessTime fontSize="inherit" /> Best Time
                                                </Typography>
                                                <Typography variant="h6" sx={{ color: '#fbbf24' }} data-testid="puzzle-best-time">
                                                    {bestLoggedTime != null ? formatTime(bestLoggedTime) : '-'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Stack>
                        )}
            play={(
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

                                {/* Puzzle Grid */}
                                <Box sx={{ position: 'relative', mb: 0 }}>
                                    <PuzzleGrid
                                        key={puzzleKey}
                                        puzzle={puzzle}
                                        isValidMove={isValidMove}
                                        makeMove={makeMove}
                                        isShowingSolution={isShowingSolution}
                                        solution={solution}
                                        solutionStep={solutionStep}
                                        getTileColor={getTileColor}
                                        GRID_SIZE={GRID_SIZE}
                                    />
                                    {isAbandoned && (
                                        <Box
                                            role="status"
                                            aria-label={IDLE_ABANDON_MESSAGE}
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                bgcolor: 'rgba(0,0,0,0.78)',
                                                backdropFilter: 'blur(4px)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                zIndex: 10,
                                                borderRadius: 3,
                                                p: 2,
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, textAlign: 'center' }}>
                                                {IDLE_ABANDON_MESSAGE}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                <GmLeaseNotice lease={gmLease} color={THEME.primary} />
                                <ManualSaveNotice notice={recorder.saveNotice} color={THEME.primary} />

                                {/* Controls */}
                                <Box
                                    data-testid="puzzle-controls"
                                    sx={{
                                        p: 2,
                                        bgcolor: COLOR.surface,
                                        border: `1px solid ${COLOR.border}`,
                                        borderRadius: 3,
                                        display: 'flex',
                                        gap: 1.5,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        mb: 3,
                                    }}
                                >
                                    {runLive ? (
                                        <UiButton variant="danger" onClick={endRun} startIcon={<Stop />} data-testid="puzzle-end">
                                            End Game
                                        </UiButton>
                                    ) : (
                                        <UiButton
                                            onClick={startGame}
                                            disabled={isSolving || isShowingSolution}
                                            startIcon={<PlayArrow data-testid="PlayArrowIcon" />}
                                            data-testid="puzzle-start"
                                        >
                                            Start
                                        </UiButton>
                                    )}
                                    <UiButton variant="secondary" onClick={resetGame} startIcon={<RestartAlt />}>
                                        Reset
                                    </UiButton>
                                    <UiButton
                                        variant="secondary"
                                        onClick={handleSolve}
                                        disabled={!isGameStarted || isGameWon || isAbandoned || isSolving || isShowingSolution}
                                        startIcon={isSolving ? <Timer /> : <Psychology />}
                                        data-testid="puzzle-ai-solve"
                                    >
                                        {isSolving
                                            ? 'Solving…'
                                            : isShowingSolution
                                                ? `${solution.length} moves`
                                                : 'AI Solve'}
                                    </UiButton>
                                    {isSolving && (
                                        <UiButton variant="ghost" onClick={cancelSolve}>
                                            Cancel
                                        </UiButton>
                                    )}
                                </Box>
                                {manualResult && !runLive && (
                                    <Box sx={{ mb: 3 }}>
                                        <ResultView
                                            title="Run ended"
                                            score={manualResult.score}
                                            details={(
                                                <Typography variant="body2" sx={{ color: COLOR.textMuted }}>
                                                    {manualResult.moves} moves
                                                </Typography>
                                            )}
                                            endedManually
                                            isGuest={isGuestUser({ token, user })}
                                            accentColor={ACCENT}
                                            onPlayAgain={startGame}
                                            autoFocus={false}
                                        />
                                    </Box>
                                )}
                                {(isSolving || solveMessage) && (
                                    <Typography variant="body2" sx={{ color: solveMessage ? THEME.accent : '#ce93d8', mb: 2, textAlign: 'center' }}>
                                        {solveMessage || solveProgress || 'Solving…'}
                                    </Typography>
                                )}
                                {isSolving && (
                                    <Box sx={{ width: '100%', maxWidth: 340, mb: 2 }}>
                                        <LinearProgress sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#ab47bc' } }} />
                                    </Box>
                                )}

                                {/* Solution Player */}
                                {isShowingSolution && (
                                    <Paper sx={{
                                        width: '100%',
                                        p: 2,
                                        bgcolor: 'rgba(156, 39, 176, 0.1)',
                                        border: '1px solid rgba(156, 39, 176, 0.3)',
                                        borderRadius: 3,
                                        mb: 3
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                            <Typography variant="subtitle2" sx={{ color: '#ce93d8' }}>
                                                {solveProgress || `Solution: ${solution.length} moves`} — Step {Math.min(solutionStep + 1, solution.length)} / {solution.length}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton size="small" onClick={autoPlay ? pauseAutoPlay : startAutoPlay} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                                                    {autoPlay ? <Pause /> : <PlayArrow />}
                                                </IconButton>
                                                <IconButton size="small" onClick={executeNextStep} disabled={autoPlay} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)' }}>
                                                    <SkipNext />
                                                </IconButton>
                                                <Button size="small" onClick={stopSolution} sx={{ color: '#ce93d8' }}>Stop</Button>
                                            </Box>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={(solutionStep / solution.length) * 100}
                                            sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { bgcolor: '#ab47bc' } }}
                                        />
                                    </Paper>
                                )}
                            </Box>
                        )}
            agent={(
                            <Stack spacing={3}>
                                <Paper sx={{
                                    p: 3,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 3
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <CloudUpload sx={{ color: THEME.primary }} />
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>Agent Mode</Typography>
                                    </Box>

                                    {(!token) ? (
                                        <Box sx={{
                                            border: `1px dashed ${COLOR.borderStrong}`,
                                            borderRadius: 2,
                                            p: 3,
                                            textAlign: 'center',
                                            mb: 2,
                                            bgcolor: 'rgba(255,255,255,0.02)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            <Lock sx={{ color: THEME.secondary, fontSize: 30 }} />
                                            <Typography variant="body2" sx={{ color: THEME.secondary }}>
                                                Sign in to upload an agent.
                                            </Typography>
                                            <Link to="/auth/login" style={{ textDecoration: 'none' }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ mt: 1, borderColor: THEME.primary, color: THEME.primary }}
                                                >
                                                    Sign in
                                                </Button>
                                            </Link>
                                        </Box>
                                    ) : (
                                        // --- REGISTERED VIEW ---
                                        <>
                                            {/* Tab switcher: Upload vs Editor */}
                                            <Box sx={{ display: 'flex', mb: 2, borderBottom: `1px solid ${THEME.border}` }}>
                                                {['upload', 'editor'].map(tab => (
                                                    <Button
                                                        key={tab}
                                                        size="small"
                                                        onClick={() => setUploadInputTab(tab)}
                                                        sx={{
                                                            flex: 1,
                                                            textTransform: 'capitalize',
                                                            borderRadius: 0,
                                                            borderBottom: uploadInputTab === tab ? `2px solid ${THEME.primary}` : '2px solid transparent',
                                                            color: uploadInputTab === tab ? THEME.primary : THEME.secondary,
                                                            fontWeight: uploadInputTab === tab ? 700 : 400,
                                                            pb: 1,
                                                        }}
                                                    >
                                                        {tab === 'upload' ? 'Upload File' : 'Code Editor'}
                                                    </Button>
                                                ))}
                                            </Box>

                                            {uploadInputTab === 'upload' ? (
                                                /* Upload file panel */
                                                <>
                                                    <Box sx={{
                                                        border: `1px dashed ${COLOR.borderStrong}`,
                                                        borderRadius: 2,
                                                        p: 2,
                                                        textAlign: 'center',
                                                        mb: 2,
                                                        bgcolor: 'rgba(255,255,255,0.02)'
                                                    }}>
                                                        <input
                                                            id="agent-upload"
                                                            type="file"
                                                            accept=".py"
                                                            style={{ display: 'none' }}
                                                            onChange={handleFileChange}
                                                        />
                                                        <label htmlFor="agent-upload">
                                                            <Button
                                                                component="span"
                                                                sx={{ color: THEME.secondary, textTransform: 'none' }}
                                                            >
                                                                {uploadFile ? uploadFile.name : 'Select Python Agent'}
                                                            </Button>
                                                        </label>
                                                    </Box>
                                                    <Button
                                                        fullWidth
                                                        variant="contained"
                                                        onClick={handleUpload}
                                                        disabled={!uploadFile || uploading}
                                                        sx={{
                                                            bgcolor: '#2563eb',
                                                            color: 'white',
                                                            mb: 2,
                                                            '&:disabled': { bgcolor: '#1e293b', color: '#64748b' }
                                                        }}
                                                    >
                                                        {uploading ? 'Uploading Agent...' : 'Upload Agent'}
                                                    </Button>
                                                </>
                                            ) : (
                                                /* Code editor panel */
                                                <AgentCodeEditor
                                                    header={PUZZLEMASTER_HEADER}
                                                    template={GAME_TEMPLATE}
                                                    footer={PUZZLEMASTER_FOOTER}
                                                    loading={uploading}
                                                    theme={THEME}
                                                    onSubmit={(code) => {
                                                        const blob = new Blob([code], { type: 'text/x-python' });
                                                        const file = new File([blob], 'agent.py', { type: 'text/x-python' });
                                                        setUploadFile(file);
                                                        setUploading(true);
                                                        setUploadNeedsAccount(false);
                                                        const formData = new FormData();
                                                        formData.append('file', file);
                                                        fetch(API_ENDPOINTS.SLIDING_TILES_UPLOAD_AGENT, {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${token}` },
                                                            body: formData
                                                        })
                                                        .then(r => r.json().catch(() => ({})).then(json => ({ r, json })))
                                                        .then(({ r, json }) => {
                                                            if (!r.ok || !json || json.error) {
                                                                const { message, needsAccount } = uploadErrorInfo(r.status, json, 'Agent run failed');
                                                                setUploadMessage(message);
                                                                setUploadNeedsAccount(!r.ok && needsAccount);
                                                                return;
                                                            }
                                                            const entry = json.entry || json.result || json;
                                                            const agentName = json.agent_id || entry.agent_id || entry.agent || 'agent_editor';
                                                            const score = json.score || entry.score || 0;
                                                            const initState = json.init_state || entry.init_state;
                                                            const moveHistory = json.move_history || entry.move_history || [];
                                                            setLastExecutedAgentName(agentName);
                                                            setLastExecutedTime(Date.now());
                                                            setLastExecutedScore(score);
                                                            if (initState) {
                                                                setLastAgentReplay({ init_state: initState, move_history: moveHistory });
                                                            }
                                                            setUploadMessage('');
                                                            fetchScoreboard();
                                                            fetchLastRun().catch(() => {});
                                                        })
                                                        .catch(e => { console.error(e); setUploadMessage('Error running agent'); })
                                                        .finally(() => setUploading(false));
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}

                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" sx={{ color: THEME.secondary, display: 'block', mb: 0.5, fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                                            Last run
                                        </Typography>
                                        {lastExecutedAgentName ? (
                                            <Paper sx={{
                                                p: 1.5,
                                                bgcolor: 'rgba(44, 187, 93, 0.1)',
                                                border: `1px solid ${THEME.primary}`,
                                                borderRadius: 2
                                            }}>
                                                <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                                                    {lastExecutedAgentName.startsWith('agent_') ? lastExecutedAgentName : `agent_${lastExecutedAgentName}`}
                                                </Typography>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        label={`Score: ${lastExecutedScore !== null ? lastExecutedScore : '-'}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: THEME.primary,
                                                            color: 'black',
                                                            fontWeight: 'bold',
                                                            height: 20,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                    <Typography variant="caption" sx={{ color: COLOR.textMuted }}>
                                                        {formatAuthTimestamp(lastExecutedTime)}
                                                    </Typography>
                                                    {shouldShowReplay(lastRunEntry, false, { only_replayable: true }) ? (
                                                        <>
                                                            <LeaderboardWatchButton
                                                                entry={lastRunEntry}
                                                                onWatch={handleViewLastPlay}
                                                                accentColor={THEME.primary}
                                                                playLabel="Play replay"
                                                                tooltipLabel="Play replay"
                                                            />
                                                            <Button
                                                                size="small"
                                                                data-testid="last-run-loop"
                                                                onClick={handleLoopLastPlay}
                                                                aria-label="Loop run"
                                                                title="Loop run"
                                                                sx={LAST_RUN_LOOP_BUTTON_SX(THEME.primary)}
                                                            >
                                                                <Loop fontSize="small" />
                                                            </Button>
                                                        </>
                                                    ) : null}
                                                </Stack>
                                            </Paper>
                                        ) : (
                                            <Paper sx={{
                                                p: 1.5,
                                                bgcolor: 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${THEME.border}`,
                                                borderRadius: 2
                                            }}>
                                                <Typography variant="caption" sx={{ color: THEME.secondary }}>
                                                    {lastRunLoading ? 'Loading...' : 'No runs yet'}
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Box>
                                    {uploadMessage && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: uploadNeedsAccount || uploadMessage.includes('failed') ? THEME.accent : THEME.primary, textAlign: 'center' }}>
                                            {uploadMessage}
                                        </Typography>
                                    )}
                                    {uploadMessage && uploadNeedsAccount && <UploadAccountLinks color={THEME.primary} />}
                                </Paper>

                                <Paper sx={{
                                    p: 3,
                                    bgcolor: THEME.cardBg,
                                    border: `1px solid ${THEME.border}`,
                                    borderRadius: 3
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <TipsAndUpdates sx={{ color: '#fbbf24' }} />
                                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>How to Play</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: THEME.secondary, lineHeight: 1.6 }}>
                                        Click tiles adjacent to the empty space to slide them. Arrange numbers 1-15 in order.
                                    </Typography>
                                </Paper>
                            </Stack>
                        )}
        >
            <Container maxWidth="xl">
                {/* Win Dialog */}
                    <Dialog
                        open={showWinDialog}
                        onClose={() => setShowWinDialog(false)}
                        PaperProps={{
                            sx: {
                                bgcolor: COLOR.surface,
                                border: `1px solid ${THEME.border}`,
                                borderRadius: 3,
                                minWidth: 300
                            }
                        }}
                    >
                        <DialogTitle sx={{ textAlign: 'center', color: isAbandoned ? THEME.accent : '#fbbf24', pt: 3 }}>
                            {!isAbandoned && <EmojiEvents fontSize="large" sx={{ mb: 1 }} />}
                            {!isAbandoned && <br />}
                            {gameOverTitle || 'Puzzle Solved!'}
                        </DialogTitle>
                        <DialogContent sx={{ textAlign: 'center', color: THEME.secondary }}>
                            <Typography variant="h4" sx={{ color: 'white', my: 1, fontWeight: 'bold' }} data-testid="puzzle-final-score">
                                {liveScore}
                            </Typography>
                            <Typography variant="body2" sx={{ color: THEME.secondary, mb: 1 }}>Score</Typography>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                                {moves} Moves
                            </Typography>
                            <Typography variant="body1">
                                Time: {formatTime(elapsedTime)}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'center', pb: 4, gap: 2 }}>
                            <Button
                                onClick={() => setShowWinDialog(false)}
                                variant="outlined"
                                sx={{ color: 'white', borderColor: COLOR.border }}
                            >
                                Close
                            </Button>
                            <Button
                                onClick={() => { setShowWinDialog(false); startGame(); }}
                                variant="contained"
                                autoFocus
                                sx={{ bgcolor: THEME.primary, color: 'black', fontWeight: 'bold' }}
                            >
                                Play Again
                            </Button>
                        </DialogActions>
                    </Dialog>
            </Container>
            <ArcadeReplayDialog
                open={replay.open}
                loading={replay.loading}
                error={replay.error}
                run={replay.run}
                startLoop={replay.startLoop}
                onClose={replay.closeReplay}
            />
        </ArcadeShellLayout>
    );
};

export {
    solvePuzzle,
    isSolvable,
    scramblePuzzle,
    applyMoves,
    createSolvedPuzzle,
    isPuzzleSolved,
} from './puzzleMasterSolver';

export {
    IDLE_ABANDON_MS,
    IDLE_ABANDON_MESSAGE,
    manualPlacementScore,
    puzzleTimeBonus,
    finalScore,
    displayedPuzzleScore,
    freezeScoreAtQuit,
    applyAiPlaybackTick,
    puzzleHistoryTick,
    isPuzzleTimerRunning,
    isIdleAbandoned,
    applyIdleTick,
    markIdleUploaded,
    shouldUploadIdleAbandon,
    humanFinishUploadArgs,
    aiSolveUploadArgs,
    idleAbandonUploadArgs,
} from './puzzleMasterRun';

export default PuzzleMaster;